/**
 * Local design-preview mock companies for Search.
 * Used in development so the spreadsheet always has a visible result set
 * without depending on a running backend.
 *
 * Logo rule: primaryLogo is set only when name, domain, and logo asset all
 * refer to the same verified organization. Otherwise primaryLogo is null and
 * EnrichmentGrid falls back to the generic company placeholder.
 */

import { companies as fixtureCompanies } from "./company";

const industry = (description) => [
  {
    levels: [
      { type: "NAICS", code: 0, description: "Sector" },
      { type: "NAICS", code: 0, description: "Group" },
      { type: "NAICS", code: 0, description: "Industry" },
      { type: "NAICS", code: 0, description: "Class" },
      { type: "NAICS", code: 0, description },
    ],
  },
];

const LOCAL_LOGO_BASE = "/Image/company-logos";

/** Verified same-origin logo assets keyed by filename (without path). */
const LOGO_FILES = {
  adt: "adt.png",
  aspendental: "aspendental.png",
  bmw: "bmw.png",
  carrier: "carrier.png",
  cdw: "cdw.png",
  coursera: "coursera.png",
  dow: "dow.png",
  fedex: "fedex.png",
  flooranddecor: "flooranddecor.png",
  generalmills: "generalmills.png",
  internationalpaper: "internationalpaper.png",
  kraftheinz: "kraftheinz.png",
  landolakes: "landolakes.png",
  mayoclinic: "mayoclinic.png",
  microsoft: "microsoft.png",
  nucor: "nucor.png",
  ppg: "ppg.png",
  salesforce: "salesforce.png",
  snowflake: "snowflake.png",
  sunrun: "sunrun.png",
  turner: "turner.png",
  ttm: "ttm.png",
  westmarine: "westmarine.png",
  xpo: "xpo.png",
};

const logoPath = (fileKey) => {
  const file = LOGO_FILES[fileKey];
  return file ? `${LOCAL_LOGO_BASE}/${file}` : null;
};

const normalizeDomain = (domain) => {
  if (!domain) return null;
  const cleaned = String(domain)
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split("/")[0]
    .trim()
    .toLowerCase();
  return cleaned || null;
};

/**
 * Registry of verified organizations used in the mock search table.
 * Each entry must keep name, domain, and logoFile aligned to one real company.
 * @type {Array<{name: string, domain: string, logoFile: string|null}>}
 */
export const VERIFIED_COMPANY_REGISTRY = [
  { name: "BMW Manufacturing", domain: "bmwmc.com", logoFile: LOGO_FILES.bmw },
  { name: "Salesforce", domain: "salesforce.com", logoFile: LOGO_FILES.salesforce },
  { name: "FedEx", domain: "fedex.com", logoFile: LOGO_FILES.fedex },
  { name: "Nucor", domain: "nucor.com", logoFile: LOGO_FILES.nucor },
  { name: "Carrier", domain: "carrier.com", logoFile: LOGO_FILES.carrier },
  { name: "Kraft Heinz", domain: "kraftheinzcompany.com", logoFile: LOGO_FILES.kraftheinz },
  { name: "Mayo Clinic", domain: "mayoclinic.org", logoFile: LOGO_FILES.mayoclinic },
  { name: "Sunrun", domain: "sunrun.com", logoFile: LOGO_FILES.sunrun },
  { name: "ADT", domain: "adt.com", logoFile: LOGO_FILES.adt },
  { name: "International Paper", domain: "internationalpaper.com", logoFile: LOGO_FILES.internationalpaper },
  { name: "Coursera", domain: "coursera.org", logoFile: LOGO_FILES.coursera },
  { name: "West Marine", domain: "westmarine.com", logoFile: LOGO_FILES.westmarine },
  { name: "Dow", domain: "dow.com", logoFile: LOGO_FILES.dow },
  { name: "PPG", domain: "ppg.com", logoFile: LOGO_FILES.ppg },
  { name: "General Mills", domain: "generalmills.com", logoFile: LOGO_FILES.generalmills },
  { name: "Snowflake", domain: "snowflake.com", logoFile: LOGO_FILES.snowflake },
  { name: "CDW", domain: "cdw.com", logoFile: LOGO_FILES.cdw },
  { name: "XPO", domain: "xpo.com", logoFile: LOGO_FILES.xpo },
  { name: "Turner Construction", domain: "turnerconstruction.com", logoFile: LOGO_FILES.turner },
  { name: "Aspen Dental", domain: "aspendental.com", logoFile: LOGO_FILES.aspendental },
  { name: "Floor & Decor", domain: "flooranddecor.com", logoFile: LOGO_FILES.flooranddecor },
  { name: "TTM Technologies", domain: "ttm.com", logoFile: LOGO_FILES.ttm },
  { name: "Land O'Lakes", domain: "landolakesinc.com", logoFile: LOGO_FILES.landolakes },
  { name: "Microsoft", domain: "microsoft.com", logoFile: LOGO_FILES.microsoft },
  { name: "Luciola Solutions", domain: "solutions.luciola-im.com", logoFile: null },
];

const registryByDomain = new Map(
  VERIFIED_COMPANY_REGISTRY.map((entry) => [normalizeDomain(entry.domain), entry])
);

const resolveVerifiedLogo = ({ name, domain }) => {
  const entry = registryByDomain.get(normalizeDomain(domain));
  if (!entry || entry.name !== name) return null;
  return entry.logoFile ? `${LOCAL_LOGO_BASE}/${entry.logoFile}` : null;
};

const verifiedCompany = ({
  id,
  name,
  domain,
  description,
  industryLabel,
  headcount,
  location,
  email,
  phone,
  revenue = null,
}) => ({
  stagedId: id,
  alphasearchId: id,
  primaryName: name,
  descriptive: [description],
  modeledClassifications: industry(industryLabel),
  harmonizedHeadcount: { pointEstimate: headcount },
  primaryLocation: { fullAddress: location },
  primaryWebsite: domain,
  emails: email ? [email] : [],
  primaryTelephone: phone || "N/A",
  revenue: revenue == null ? null : { pointEstimate: revenue },
  primaryLogo: resolveVerifiedLogo({ name, domain }),
});

const normalizeFixtureCompany = (raw, index) => {
  const domain = raw.primaryWebsite || "";
  const id = raw.stagedId || raw.alphasearchId || `fixture-${index}`;
  const emailsFromPeople = Array.isArray(raw.people)
    ? raw.people.map((person) => person?.email).filter(Boolean)
    : [];
  const name = raw.primaryName || "";
  return {
    ...raw,
    stagedId: id,
    alphasearchId: raw.alphasearchId || raw.stagedId || `fixture-${index}`,
    emails: Array.isArray(raw.emails) && raw.emails.length ? raw.emails : emailsFromPeople,
    primaryLogo: resolveVerifiedLogo({ name, domain }),
  };
};

/** Verified real companies with matching name, domain, and logo. */
const VERIFIED_MOCK_COMPANIES = [
  verifiedCompany({
    id: "verified-salesforce",
    name: "Salesforce",
    description:
      "Cloud-based CRM platform helping sales, service, marketing, and IT teams work as one.",
    industryLabel: "Software Publishers",
    headcount: 72682,
    location: "415 Mission St, San Francisco, CA 94105",
    domain: "salesforce.com",
    email: "press@salesforce.com",
    phone: "+1 415 901 7000",
    revenue: 34857000000,
  }),
  verifiedCompany({
    id: "verified-fedex",
    name: "FedEx",
    description:
      "Global transportation, e-commerce, and business services company operating express delivery and logistics networks.",
    industryLabel: "Couriers and Express Delivery Services",
    headcount: 505000,
    location: "942 South Shady Grove Rd, Memphis, TN 38120",
    domain: "fedex.com",
    email: "mediarelations@fedex.com",
    phone: "+1 901 818 7500",
    revenue: 87693000000,
  }),
  verifiedCompany({
    id: "verified-nucor",
    name: "Nucor",
    description:
      "Steel and steel products manufacturer operating electric arc furnace mills across North America.",
    industryLabel: "Iron and Steel Mills and Ferroalloy Manufacturing",
    headcount: 31100,
    location: "1915 Rexford Rd, Charlotte, NC 28211",
    domain: "nucor.com",
    email: "info@nucor.com",
    phone: "+1 704 366 7000",
    revenue: 34414000000,
  }),
  verifiedCompany({
    id: "verified-carrier",
    name: "Carrier",
    description:
      "Global provider of HVAC, refrigeration, and fire and security solutions for residential and commercial buildings.",
    industryLabel: "Air-Conditioning and Warm Air Heating Equipment Manufacturing",
    headcount: 48000,
    location: "13995 Pasteur Blvd, Palm Beach Gardens, FL 33418",
    domain: "carrier.com",
    email: "media@carrier.com",
    phone: "+1 561 365 2000",
    revenue: 22398000000,
  }),
  verifiedCompany({
    id: "verified-kraft-heinz",
    name: "Kraft Heinz",
    description:
      "Food and beverage company producing packaged foods, condiments, and meal solutions for global retail and foodservice channels.",
    industryLabel: "Food Manufacturing",
    headcount: 37000,
    location: "1 PPG Place, Pittsburgh, PA 15222",
    domain: "kraftheinzcompany.com",
    email: "media@kraftheinzcompany.com",
    phone: "+1 412 456 5700",
    revenue: 26640000000,
  }),
  verifiedCompany({
    id: "verified-mayo-clinic",
    name: "Mayo Clinic",
    description:
      "Nonprofit academic medical center providing integrated clinical practice, education, and research.",
    industryLabel: "General Medical and Surgical Hospitals",
    headcount: 76000,
    location: "200 First St SW, Rochester, MN 55905",
    domain: "mayoclinic.org",
    email: "newsroom@mayo.edu",
    phone: "+1 507 284 2511",
    revenue: 16800000000,
  }),
  verifiedCompany({
    id: "verified-sunrun",
    name: "Sunrun",
    description:
      "Residential solar, battery storage, and energy services company serving homeowners across the United States.",
    industryLabel: "Solar Electric Power Generation",
    headcount: 4200,
    location: "225 Bush St, San Francisco, CA 94104",
    domain: "sunrun.com",
    email: "press@sunrun.com",
    phone: "+1 855 478 6786",
    revenue: 2290000000,
  }),
  verifiedCompany({
    id: "verified-adt",
    name: "ADT",
    description:
      "Provider of security, interactive smart home, and small business automation services in North America.",
    industryLabel: "Security Systems Services",
    headcount: 14000,
    location: "1501 Yamato Rd, Boca Raton, FL 33431",
    domain: "adt.com",
    email: "media@adt.com",
    phone: "+1 800 238 2727",
    revenue: 4900000000,
  }),
  verifiedCompany({
    id: "verified-international-paper",
    name: "International Paper",
    description:
      "Producer of renewable fiber-based packaging, pulp, and paper products for global supply chains.",
    industryLabel: "Paper Mills",
    headcount: 65000,
    location: "6400 Poplar Ave, Memphis, TN 38197",
    domain: "internationalpaper.com",
    email: "media@ipaper.com",
    phone: "+1 901 419 9000",
    revenue: 18900000000,
  }),
  verifiedCompany({
    id: "verified-coursera",
    name: "Coursera",
    description:
      "Online learning platform offering courses, certificates, and degrees from universities and industry partners.",
    industryLabel: "Educational Support Services",
    headcount: 1200,
    location: "381 E Evelyn Ave, Mountain View, CA 94041",
    domain: "coursera.org",
    email: "press@coursera.org",
    phone: "+1 650 963 9884",
    revenue: 635000000,
  }),
  verifiedCompany({
    id: "verified-west-marine",
    name: "West Marine",
    description:
      "Specialty retailer of boating supplies, marine electronics, and maintenance products.",
    industryLabel: "Other Miscellaneous Durable Goods Merchant Wholesalers",
    headcount: 5000,
    location: "500 Westridge Dr, Watsonville, CA 95076",
    domain: "westmarine.com",
    email: "customerservice@westmarine.com",
    phone: "+1 831 728 2700",
    revenue: 670000000,
  }),
  verifiedCompany({
    id: "verified-dow",
    name: "Dow",
    description:
      "Materials science company supplying plastics, industrial intermediates, and coatings for global markets.",
    industryLabel: "Petrochemical Manufacturing",
    headcount: 35900,
    location: "2211 H.H. Dow Way, Midland, MI 48674",
    domain: "dow.com",
    email: "media@dow.com",
    phone: "+1 989 636 1000",
    revenue: 44622000000,
  }),
  verifiedCompany({
    id: "verified-ppg",
    name: "PPG",
    description:
      "Manufacturer of paints, coatings, and specialty materials for automotive, aerospace, and construction markets.",
    industryLabel: "Paint and Coating Manufacturing",
    headcount: 46000,
    location: "1 PPG Place, Pittsburgh, PA 15272",
    domain: "ppg.com",
    email: "media.ppg@ppg.com",
    phone: "+1 412 434 3131",
    revenue: 18246000000,
  }),
  verifiedCompany({
    id: "verified-general-mills",
    name: "General Mills",
    description:
      "Consumer foods company producing branded packaged foods for retail and foodservice customers worldwide.",
    industryLabel: "Food Manufacturing",
    headcount: 34000,
    location: "1 General Mills Blvd, Minneapolis, MN 55426",
    domain: "generalmills.com",
    email: "media.generalmills@genmills.com",
    phone: "+1 763 764 7600",
    revenue: 20094000000,
  }),
  verifiedCompany({
    id: "verified-snowflake",
    name: "Snowflake",
    description:
      "Cloud data platform enabling storage, processing, and analytics across public cloud environments.",
    industryLabel: "Custom Computer Programming Services",
    headcount: 7000,
    location: "106 East Babcock St, Bozeman, MT 59715",
    domain: "snowflake.com",
    email: "press@snowflake.com",
    phone: "+1 844 766 9355",
    revenue: 2800000000,
  }),
  verifiedCompany({
    id: "verified-cdw",
    name: "CDW",
    description:
      "Technology solutions provider selling hardware, software, and services to business, government, and education customers.",
    industryLabel: "Computer Systems Design",
    headcount: 15100,
    location: "75 Tri-State International, Lincolnshire, IL 60069",
    domain: "cdw.com",
    email: "media@cdw.com",
    phone: "+1 847 465 6000",
    revenue: 20700000000,
  }),
  verifiedCompany({
    id: "verified-xpo",
    name: "XPO",
    description:
      "Transportation and logistics provider offering less-than-truckload, truck brokerage, and supply chain services.",
    industryLabel: "Freight Transportation Arrangement",
    headcount: 38000,
    location: "Five American Ln, Greenwich, CT 06831",
    domain: "xpo.com",
    email: "media@xpo.com",
    phone: "+1 855 976 6951",
    revenue: 7760000000,
  }),
  verifiedCompany({
    id: "verified-turner",
    name: "Turner Construction",
    description:
      "North American construction management firm delivering commercial, healthcare, and infrastructure projects.",
    industryLabel: "Commercial Building Construction",
    headcount: 10000,
    location: "375 Hudson St, New York, NY 10014",
    domain: "turnerconstruction.com",
    email: "communications@turnerconstruction.com",
    phone: "+1 212 229 6000",
    revenue: 14000000000,
  }),
  verifiedCompany({
    id: "verified-aspen-dental",
    name: "Aspen Dental",
    description:
      "Dental support organization operating branded dental practices across the United States.",
    industryLabel: "Offices of Dentists",
    headcount: 20000,
    location: "800 West College Dr, East Syracuse, NY 13057",
    domain: "aspendental.com",
    email: "media@aspendental.com",
    phone: "+1 315 464 0100",
    revenue: 3500000000,
  }),
  verifiedCompany({
    id: "verified-floor-decor",
    name: "Floor & Decor",
    description:
      "Specialty retailer of hard-surface flooring, decorative tile, and related installation materials.",
    industryLabel: "Home Centers",
    headcount: 11000,
    location: "2500 Windy Ridge Pkwy SE, Atlanta, GA 30339",
    domain: "flooranddecor.com",
    email: "investorrelations@flooranddecor.com",
    phone: "+1 404 471 1000",
    revenue: 4300000000,
  }),
  verifiedCompany({
    id: "verified-ttm",
    name: "TTM Technologies",
    description:
      "Manufacturer of printed circuit boards and radio frequency components for aerospace, defense, and medical markets.",
    industryLabel: "Bare Printed Circuit Board Manufacturing",
    headcount: 16000,
    location: "200 East Sandpointe Ave, Santa Ana, CA 92707",
    domain: "ttm.com",
    email: "investorrelations@ttm.com",
    phone: "+1 714 327 3000",
    revenue: 2300000000,
  }),
  verifiedCompany({
    id: "verified-land-olakes",
    name: "Land O'Lakes",
    description:
      "Farmer-owned cooperative producing dairy foods, animal nutrition, and crop inputs.",
    industryLabel: "Dairy Product Manufacturing",
    headcount: 10000,
    location: "4001 Lexington Ave N, Arden Hills, MN 55126",
    domain: "landolakesinc.com",
    email: "media@landolakes.com",
    phone: "+1 651 375 2222",
    revenue: 19000000000,
  }),
  verifiedCompany({
    id: "verified-microsoft",
    name: "Microsoft",
    description:
      "Technology company providing cloud platforms, productivity software, devices, and enterprise services.",
    industryLabel: "Software Publishers",
    headcount: 228000,
    location: "1 Microsoft Way, Redmond, WA 98052",
    domain: "microsoft.com",
    email: "press@microsoft.com",
    phone: "+1 425 882 8080",
    revenue: 245122000000,
  }),
];

const fixtures = fixtureCompanies.map(normalizeFixtureCompany);
const bmwFixture = fixtures.find((row) => row.primaryName === "BMW Manufacturing");
const luciolaFixture = fixtures.find((row) => row.primaryName === "Luciola Solutions");

/** Verified-logo rows first; Luciola keeps placeholder because official logo is unverified offline. */
export const MOCK_SEARCH_COMPANIES = [
  ...(bmwFixture ? [bmwFixture] : []),
  ...VERIFIED_MOCK_COMPANIES,
  ...(luciolaFixture ? [luciolaFixture] : []),
];

export const isMockSearchCompaniesEnabled = () =>
  process.env.REACT_APP_USE_MOCK_SEARCH_COMPANIES === "true";

/** @internal test helper */
export const __mockLogoHelpers = {
  resolveVerifiedLogo,
  VERIFIED_COMPANY_REGISTRY,
  LOGO_FILES,
  logoPath,
};
